import { useEffect, useRef, useState } from 'react'
import api from '../services/api'
import { getSocket } from '../services/socket'

/** Keys the current user may see in `serverId`; refetches on the
 *  server:moduleAccess:updated + server:modules:updated socket events.
 *  null = INITIAL loading / fail-open (caller then does not filter).
 *
 *  OSPROD-237 (Dashboard section flickers in/out): the rail was intermittently
 *  hiding/showing admin-gated modules. Two client-side transients caused it,
 *  both fixed here:
 *   1. On a refetch FAILURE the hook used to reset to `null` → the caller
 *      treats null as fail-open ("show everything"), so a transient network
 *      blip briefly flashed admin-only modules to everyone AND flickered the
 *      rail. Now a failure KEEPS the last-known set; we only stay null before
 *      the first successful load (where fail-open is the intended default).
 *   2. The socket events fire on nearly every server action, so overlapping
 *      GETs are in flight; an out-of-order (stale) response could overwrite a
 *      fresh set, dropping a module until the next event. A monotonic request
 *      id now lets ONLY the latest in-flight load commit its result.
 *   3. (round 2 — non-admin flicker) A transient `serverId === undefined` during
 *      navigation reset keys to null → fail-open flashed admin-only modules
 *      (e.g. Dashboard) to a NON-admin, then hid them on resolve. Now we KEEP
 *      the last-known set on a transient serverId loss, and we SEED keys from a
 *      per-server localStorage cache so a repeat visit / server switch shows the
 *      correct set immediately (no fail-open window). Only a brand-new browser's
 *      first-ever load for a server still fails open for the ~1 network RTT. */
const cacheKeyFor = (serverId: string) => `visMods:${serverId}`
function readVisCache(serverId: string | undefined): Set<string> | null {
  if (!serverId) return null
  try {
    const raw = localStorage.getItem(cacheKeyFor(serverId))
    return raw ? new Set(JSON.parse(raw) as string[]) : null
  } catch { return null }
}

export function useVisibleModules(serverId: string | undefined): Set<string> | null {
  const [keys, setKeys] = useState<Set<string> | null>(() => readVisCache(serverId))
  // Only the latest load() may commit — guards against out-of-order refetches.
  const reqSeq = useRef(0)
  useEffect(() => {
    // Transient serverId loss during navigation: KEEP the last-known set rather
    // than resetting to null (which fails open and flashes admin-only modules).
    if (!serverId) return
    let cancelled = false
    // Seed synchronously from cache so a server switch never flashes fail-open.
    const cached = readVisCache(serverId)
    if (cached) setKeys(cached)
    const load = () => {
      const myReq = ++reqSeq.current
      return api.get(`/servers/${serverId}/visible-modules`)
        .then((r) => {
          if (cancelled || myReq !== reqSeq.current) return
          setKeys(new Set(r.data.keys))
          try { localStorage.setItem(cacheKeyFor(serverId), JSON.stringify(r.data.keys)) } catch { /* quota / private mode */ }
        })
        .catch(() => {
          // Keep the last-known set — do NOT fall back to null (fail-open) on a
          // transient refetch failure. Initial `keys` is already null, so the
          // first-load-failure case still fails open by default.
        })
    }
    load()
    const socket = getSocket()
    const onUpd = (p: { serverId: string }) => { if (p.serverId === serverId) load() }
    socket.on('server:moduleAccess:updated', onUpd)
    socket.on('server:modules:updated', onUpd)
    return () => {
      cancelled = true
      socket.off('server:moduleAccess:updated', onUpd)
      socket.off('server:modules:updated', onUpd)
    }
  }, [serverId])
  return keys
}
